import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { get } from "@/lib/api";
import { PageLoader, PageHeader, EmptyState, Badge, statusTone, StatCard } from "@/components/common";
import TestDetailModal from "@/components/TestDetailModal";
import { FileText, ClipboardList, Filter } from "lucide-react";

const TYPE_LABEL = { mock: "Full Mock", pyp: "Previous Year Paper", practice: "Practice Set", daily_quiz: "Daily Quiz", subject: "Subject Test", chapter: "Chapter Test" };

export default function TeacherMocks() {
  const [typeFilter, setTypeFilter] = useState("");
  const [examFilter, setExamFilter] = useState("");
  const [detailId, setDetailId] = useState(null);
  const { data: tests, isLoading, refetch } = useQuery({ queryKey: ["mocks-mine"], queryFn: () => get("/tests/mine") });
  const { data: tax } = useQuery({ queryKey: ["tax-all"], queryFn: () => get("/taxonomy") });

  const taxMap = useMemo(() => Object.fromEntries((tax || []).map((n) => [n.id, n])), [tax]);
  const nameOf = (id) => taxMap[id]?.name || "—";
  const categoryOf = (examId) => {
    let node = taxMap[examId];
    while (node && node.level !== "category" && node.parent_id) node = taxMap[node.parent_id];
    return node?.level === "category" ? node.name : "—";
  };

  const list = (tests || []).filter((t) => (!typeFilter || t.test_type === typeFilter) && (!examFilter || t.exam_id === examFilter));
  const exams = [...new Set((tests || []).map((t) => t.exam_id).filter(Boolean))];
  const stats = {
    total: (tests || []).length,
    mock: (tests || []).filter((t) => t.test_type === "mock").length,
    pyp: (tests || []).filter((t) => t.test_type === "pyp").length,
    practice: (tests || []).filter((t) => ["practice", "daily_quiz"].includes(t.test_type)).length,
  };

  return (
    <div>
      <PageHeader title="Mock Tests" subtitle="Every mock, PYP and practice set you have uploaded — with full details" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard icon={FileText} label="Total Uploaded" value={stats.total} tone="indigo" />
        <StatCard icon={FileText} label="Full Mocks" value={stats.mock} tone="violet" />
        <StatCard icon={ClipboardList} label="PYP" value={stats.pyp} tone="amber" />
        <StatCard icon={ClipboardList} label="Practice / Quiz" value={stats.practice} tone="jade" />
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4 bg-white border border-slate-200 rounded-xl p-3">
        <Filter className="h-4 w-4 text-slate-400" />
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} data-testid="mock-type-filter" className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm">
          <option value="">All types</option>
          {Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={examFilter} onChange={(e) => setExamFilter(e.target.value)} data-testid="mock-exam-filter" className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm">
          <option value="">All exams</option>
          {exams.map((id) => <option key={id} value={id}>{nameOf(id)}</option>)}
        </select>
        {(typeFilter || examFilter) && <button onClick={() => { setTypeFilter(""); setExamFilter(""); }} className="text-xs text-indigo-700 font-semibold">Clear</button>}
      </div>

      {isLoading ? <PageLoader /> : list.length === 0 ? <EmptyState icon={FileText} title="No mocks found" hint="Create tests via Test Builder or OCR Import." /> : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>
              <th className="text-left p-3">Title</th><th className="text-left p-3">Type</th><th className="text-left p-3">Category</th><th className="text-left p-3">Exam</th><th className="text-left p-3">Qs</th><th className="text-left p-3">Status</th></tr></thead>
            <tbody>{list.map((t) => (
              <tr key={t.id} onClick={() => setDetailId(t.id)} data-testid={`mock-row-${t.id}`} className="border-t border-slate-100 cursor-pointer hover:bg-slate-50">
                <td className="p-3 font-medium text-slate-800">{t.title}</td>
                <td className="p-3"><Badge tone="indigo">{TYPE_LABEL[t.test_type] || t.test_type}</Badge></td>
                <td className="p-3 text-slate-600">{categoryOf(t.exam_id)}</td>
                <td className="p-3 text-slate-600">{nameOf(t.exam_id)}</td>
                <td className="p-3 text-slate-600">{t.total_questions}</td>
                <td className="p-3"><Badge tone={statusTone(t.status)}>{t.status}</Badge></td>
              </tr>))}</tbody>
          </table>
        </div>
      )}
      {detailId && <TestDetailModal testId={detailId} canManage onClose={() => setDetailId(null)} onChanged={refetch} />}
    </div>
  );
}
